import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider as BaseQueryClientProvider } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { isSessionExpiredError } from '../../../utilities/errors/isUnauthorizedError';
import { getUnauthorizedReason } from '../../../utilities/errors/unauthorizedReason';
import { holdOfflineCopy, isOfflineCopy, loadOfflineCopyQueries, startOfflineQueryCache } from '../../offline';
import { notifySessionExpired } from '../../session-management/sessionExpiryCoordinator';
import { registerReconnectCoordinator } from './reconnectCoordinator';
const ReactQueryDevtools = process.env.NODE_ENV === 'development' ? dynamic(() => import('@tanstack/react-query-devtools').then(mod => mod.ReactQueryDevtools), {
  ssr: false
}) : null;

/**
 * Routes any session-expiry 401 surfaced by a query/mutation to the global
 * session-expiry coordinator so the app can sign the user out gracefully
 * instead of dumping them on a raw "Unauthorized" error screen.
 */
function handleGlobalQueryError(error: unknown): void {
  if (!isSessionExpiredError(error)) {
    return;
  }
  notifySessionExpired(getUnauthorizedReason(error) ?? 'unknown');
}
type SidecarConfig = {
  reactQueryDevtools?: {
    disabled?: boolean;
    position?: 'bottom-right' | 'top-left' | 'top-right' | 'bottom-left';
  };
};
export const QueryClientProvider: React.FC<React.PropsWithChildren<{
  reactQueryDevtools?: SidecarConfig['reactQueryDevtools'];
}>> = t0 => {
  const $ = _c(16);
  const {
    reactQueryDevtools,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isOfflineCopy();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const offlineCopy = t1;
  const offlineCopyQueries = offlineCopy ? React.use(loadOfflineCopyQueries()) : undefined;
  let t2;
  if ($[1] !== offlineCopyQueries) {
    t2 = () => {
      const client = new QueryClient({
        queryCache: new QueryCache({
          onError: handleGlobalQueryError
        }),
        mutationCache: new MutationCache({
          onError: handleGlobalQueryError
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
            refetchOnReconnect: false,
            networkMode: "always"
          },
          mutations: {
            retry: false,
            networkMode: "always"
          }
        }
      });
      if (offlineCopy) {
        holdOfflineCopy(client, offlineCopyQueries);
      }
      return client;
    };
    $[1] = offlineCopyQueries;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [queryClient] = React.useState(t2);
  let t3;
  let t4;
  if ($[3] !== queryClient) {
    t3 = () => registerReconnectCoordinator(queryClient);
    t4 = [queryClient];
    $[3] = queryClient;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== queryClient) {
    t5 = () => startOfflineQueryCache(queryClient);
    t6 = [queryClient];
    $[6] = queryClient;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[9] !== reactQueryDevtools?.disabled || $[10] !== reactQueryDevtools?.position) {
    t7 = !reactQueryDevtools?.disabled && ReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} buttonPosition={reactQueryDevtools?.position || "bottom-right"} />;
    $[9] = reactQueryDevtools?.disabled;
    $[10] = reactQueryDevtools?.position;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== children || $[13] !== queryClient || $[14] !== t7) {
    t8 = <BaseQueryClientProvider client={queryClient}>{t7}{children}</BaseQueryClientProvider>;
    $[12] = children;
    $[13] = queryClient;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};